import React from "react";
import { Tag as TagIcon, Plus, X } from "lucide-react";
import { ChangeTypes, StatusTypes } from "../../config/auditLogConfig.js";

// Sub-form components
import { RoutingChangeForm } from "./changeForms/RoutingChangeForm.jsx";
import { ABTestForm } from "./changeForms/ABTestForm.jsx";

const field =
  "block w-full rounded-lg border border-border bg-background px-3 py-2 " +
  "text-foreground placeholder-muted-foreground focus:outline-none " +
  "focus:ring-2 focus:ring-brand/40 focus:border-brand/50";

export function CreateLogForm({ formData, setFormData, newTag, setNewTag }) {
  // ----- Tag handlers -----
  const addTag = () => {
    const t = newTag.trim();
    if (t && !formData.tags.includes(t)) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, t] }));
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  return (
    <div className="p-6 space-y-8">
      {/* Basic Information */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Basic Information
          </h3>
          <span className="text-xs text-muted-foreground">* Required</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="space-y-1 md:col-span-2">
            <span className="text-sm font-medium">Title *</span>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              className={field}
              placeholder="e.g., BIN 410039 → Quantum (NMI)"
            />
          </label>

          <label className="space-y-1">
            <span className="text-sm font-medium">Status *</span>
            <select
              required
              value={formData.status}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, status: e.target.value }))
              }
              className={field}
            >
              {Object.values(StatusTypes).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-sm font-medium">Change Type *</span>
            <select
              required
              value={formData.changeType}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, changeType: e.target.value }))
              }
              className={field}
            >
              {Object.values(ChangeTypes).map((ct) => (
                <option key={ct} value={ct}>
                  {ct}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* Conditionally render sub-forms */}
      {formData.changeType === ChangeTypes.ROUTING_CHANGE && (
        <RoutingChangeForm formData={formData} setFormData={setFormData} />
      )}

      {formData.changeType === ChangeTypes.AB_TEST && (
        <ABTestForm formData={formData} setFormData={setFormData} />
      )}

      {/* Tags */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Tags
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <TagIcon className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              className={`${field} pl-9`}
              placeholder="Add a tag"
            />
          </div>
          <button
            type="button"
            onClick={addTag}
            className="btn btn-ghost inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>

        {formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag) => (
              <span
                key={tag}
                className="badge badge-brand inline-flex items-center gap-2"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="opacity-80 hover:opacity-100"
                  aria-label={`Remove ${tag}`}
                  title="Remove tag"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
